import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { pierres } from '../mock';

const FichePierre = () => {
  const { id } = useParams();
  const pierre = pierres.find((p) => p.id === parseInt(id));

  if (!pierre) {
    return (
      <div className="not-found">
        <h1>Pierre non trouvée</h1>
        <Link to="/encyclopedie" className="back-link">Retour à l'encyclopédie</Link>
      </div>
    );
  }

  return (
    <main className="fiche-container">
      <Link to="/encyclopedie" className="back-button">
        <ArrowLeft size={20} />
        Retour à l'encyclopédie
      </Link>

      <div className="fiche-content">
        {/* Image and Main Info */}
        <div className="fiche-hero">
          <div className="image-section">
            <img src={pierre.image} alt={pierre.nom} className="pierre-main-image" />
          </div>

          <div className="main-info-section">
            <h1 className="pierre-title">{pierre.nom}</h1>
            <p className="pierre-subtitle">{pierre.nomScientifique}</p>

            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Formule chimique</span>
                <span className="info-value formula">{pierre.formuleChimique}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Système cristallin</span>
                <span className="info-value">{pierre.systemeCristallin}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Dureté (Mohs)</span>
                <span className="info-value">{pierre.dureteMohs}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Couleur</span>
                <span className="info-value">{pierre.couleur}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Origine</span>
                <span className="info-value">{pierre.origine}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Histoire */}
        <section className="content-section">
          <h2 className="section-heading">Histoire & Origine</h2>
          <p className="section-text">{pierre.histoire}</p>
        </section>

        {/* Propriétés énergétiques */}
        <section className="content-section highlighted">
          <div className="section-header-with-icon">
            <Sparkles size={28} className="section-icon" />
            <h2 className="section-heading">Propriétés Énergétiques</h2>
          </div>
          <p className="section-text">{pierre.propriétésÉnergétiques}</p>
        </section>

        {/* Chakras et Signes */}
        <div className="two-column-section">
          <section className="content-section">
            <h2 className="section-heading">Chakras associés</h2>
            <div className="tags-container">
              {pierre.chakras.map((chakra, index) => (
                <span key={index} className="chakra-tag">{chakra}</span>
              ))}
            </div>
          </section>

          <section className="content-section">
            <h2 className="section-heading">Signes astrologiques</h2>
            <div className="tags-container">
              {pierre.signesAstrologiques.map((signe, index) => (
                <span key={index} className="signe-tag">{signe}</span>
              ))}
            </div>
          </section>
        </div>

        {/* Intentions */}
        <section className="content-section">
          <h2 className="section-heading">Intentions & Usages</h2>
          <div className="tags-container">
            {pierre.intentions.map((intention, index) => (
              <span key={index} className="intention-tag">{intention}</span>
            ))}
          </div>
        </section>

        {/* Entretien */}
        <div className="two-column-section">
          <section className="content-section">
            <h2 className="section-heading">Purification</h2>
            <p className="section-text">{pierre.purification}</p>
          </section>

          <section className="content-section">
            <h2 className="section-heading">Rechargement</h2>
            <p className="section-text">{pierre.rechargement}</p>
          </section>
        </div>

        {/* Disclaimer */}
        <div className="disclaimer">
          <p>
            <strong>Important :</strong> Les informations présentées sur cette page sont à titre informatif et éducatif. 
            La lithothérapie ne remplace en aucun cas un traitement médical. Consultez toujours un professionnel de santé qualifié.
          </p>
        </div>
      </div>

      <style jsx>{`
        .fiche-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem;
          min-height: 80vh;
        }

        .back-button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          color: #2d5a8c;
          text-decoration: none;
          font-weight: 600;
          margin-bottom: 2rem;
          padding: 0.75rem 0;
          transition: gap 0.2s ease;
        }

        .back-button:hover {
          gap: 0.75rem;
          transform: none;
        }

        .fiche-content {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        }

        .fiche-hero {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0;
        }

        .image-section {
          position: relative;
          padding-top: 100%;
          background: #f7fafc;
        }

        .pierre-main-image {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .main-info-section {
          padding: 3rem;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .pierre-title {
          font-size: 3rem;
          color: #1a365d;
          margin-bottom: 0.5rem;
          font-weight: 700;
        }

        .pierre-subtitle {
          font-size: 1.25rem;
          color: #718096;
          font-style: italic;
          margin-bottom: 2.5rem;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.5rem;
        }

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
        }

        .info-label {
          font-size: 0.85rem;
          color: #718096;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .info-value {
          font-size: 1.1rem;
          color: #2d3748;
          font-weight: 600;
        }

        .info-value.formula {
          font-family: 'Courier New', monospace;
          color: #2d5a8c;
        }

        .content-section {
          padding: 2.5rem 3rem;
          border-top: 1px solid #e2e8f0;
        }

        .content-section.highlighted {
          background: rgba(45, 90, 140, 0.03);
        }

        .section-header-with-icon {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .section-icon {
          color: #2d5a8c;
        }

        .section-heading {
          font-size: 1.75rem;
          color: #1a365d;
          margin-bottom: 1rem;
          font-weight: 600;
        }

        .section-text {
          font-size: 1.05rem;
          line-height: 1.8;
          color: #4a5568;
        }

        .two-column-section {
          display: grid;
          grid-template-columns: 1fr 1fr;
          border-top: 1px solid #e2e8f0;
        }

        .two-column-section .content-section {
          border-top: none;
        }

        .two-column-section .content-section:first-child {
          border-right: 1px solid #e2e8f0;
        }

        .tags-container {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
        }

        .chakra-tag,
        .signe-tag,
        .intention-tag {
          padding: 0.6rem 1.25rem;
          border-radius: 25px;
          font-weight: 600;
          font-size: 0.95rem;
        }

        .chakra-tag {
          background: rgba(45, 90, 140, 0.1);
          color: #2d5a8c;
        }

        .signe-tag {
          background: rgba(106, 155, 195, 0.15);
          color: #2d5a8c;
        }

        .intention-tag {
          background: rgba(74, 123, 167, 0.1);
          color: #2d5a8c;
        }

        .disclaimer {
          padding: 2rem 3rem;
          background: rgba(237, 137, 54, 0.05);
          border-top: 1px solid #e2e8f0;
        }

        .disclaimer p {
          color: #744210;
          line-height: 1.7;
          font-size: 0.95rem;
        }

        .disclaimer strong {
          color: #7c2d12;
        }

        .not-found {
          max-width: 600px;
          margin: 5rem auto;
          text-align: center;
          padding: 2rem;
        }

        .not-found h1 {
          color: #1a365d;
          margin-bottom: 1.5rem;
        }

        .back-link {
          color: #2d5a8c;
          text-decoration: none;
          font-weight: 600;
        }

        @media (max-width: 768px) {
          .fiche-container {
            padding: 1.5rem;
          }

          .fiche-hero {
            grid-template-columns: 1fr;
          }

          .main-info-section {
            padding: 2rem 1.5rem;
          }

          .pierre-title {
            font-size: 2rem;
          }

          .pierre-subtitle {
            font-size: 1.05rem;
          }

          .info-grid {
            grid-template-columns: 1fr;
            gap: 1.25rem;
          }

          .content-section {
            padding: 2rem 1.5rem;
          }

          .section-heading {
            font-size: 1.4rem;
          }

          .two-column-section {
            grid-template-columns: 1fr;
          }

          .two-column-section .content-section:first-child {
            border-right: none;
            border-bottom: 1px solid #e2e8f0;
          }

          .disclaimer {
            padding: 1.5rem;
          }
        }
      `}</style>
    </main>
  );
};

export default FichePierre;